import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Shield, Brain, BarChart3, Calendar, TrendingUp } from 'lucide-react';

const features = [
  {
    id: 'rate-parity',
    icon: Shield,
    title: 'Rate Parity',
    badge: 'Core Feature',
    description: 'Monitor your rates across all OTA channels and detect undercuts in real-time.',
    image: 'https://images.pexels.com/photos/669612/pexels-photo-669612.jpeg?auto=compress&cs=tinysrgb&w=800',
    benefits: [
      'Track 15+ OTA channels in real-time',
      'Instant undercut detection alerts',
      'Historical rate parity trends',
      'OTA-wise compliance scoring',
      'Revenue impact analysis',
    ],
  },
  {
    id: 'ai-insights',
    icon: Brain,
    title: 'AI Insights',
    badge: 'Prowen AI',
    description: 'Get intelligent pricing recommendations and market analysis powered by Prowen AI.',
    image: 'https://images.pexels.com/photos/572056/pexels-photo-572056.jpeg?auto=compress&cs=tinysrgb&w=800',
    benefits: [
      'AI-powered pricing recommendations',
      'Market trend analysis',
      'Competitive positioning insights',
      'Revenue optimization suggestions',
      'Natural language summaries',
    ],
  },
  {
    id: 'competitive',
    icon: BarChart3,
    title: 'Competitive Analysis',
    badge: 'Market Intel',
    description: 'Understand your market position with comprehensive competitor tracking.',
    image: 'https://images.unsplash.com/photo-1660557989695-14fac79c086d?auto=format&fit=crop&w=800',
    benefits: [
      'Real-time competitor rate tracking',
      'Price vs rating analysis',
      'Market position ranking',
      'Amenity comparison heatmaps',
      'Competitive advantage scoring',
    ],
  },
  {
    id: 'demand',
    icon: Calendar,
    title: 'Demand Calendar',
    badge: 'Events & Trends',
    description: 'Plan ahead with demand forecasting and local event tracking.',
    image: 'https://images.pexels.com/photos/7014337/pexels-photo-7014337.jpeg?auto=compress&cs=tinysrgb&w=800',
    benefits: [
      'Local events & conferences tracking',
      'Public holiday calendar',
      'Demand impact indicators',
      'Ticketmaster event integration',
      'Google Calendar sync',
    ],
  },
];

export default function FeaturesSection() {
  const [activeTab, setActiveTab] = useState('rate-parity');

  const activeFeature = features.find(f => f.id === activeTab);

  return (
    <section id="solutions" className="py-20 lg:py-28 section-gradient">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-3">
            Key Capabilities
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Advanced Hotel Analytics and Price Intelligence for Every Need
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Leverage hotel analytics, competitor pricing insights, and AI-driven intelligence to improve pricing decisions and boost revenue performance.
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex flex-wrap justify-center gap-2 bg-transparent mb-12 p-0">
            {features.map((feature) => (
              <TabsTrigger
                key={feature.id}
                value={feature.id}
                className="flex items-center gap-2 px-6 py-3 rounded-xl border border-border bg-card data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary transition-all duration-300 shadow-sm data-[state=active]:shadow-md"
              >
                <feature.icon className="w-4 h-4" />
                {feature.title}
              </TabsTrigger>
            ))}
          </TabsList>

          {features.map((feature) => (
            <TabsContent key={feature.id} value={feature.id} className="mt-0">
              <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                {/* Content */}
                <div>
                  <Badge variant="outline" className="mb-4 bg-accent/10 text-accent border-accent/30">
                    {feature.badge}
                  </Badge>
                  <h3 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                    {feature.description}
                  </p>
                  
                  <ul className="space-y-4">
                    {feature.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-success" />
                        </div>
                        <span className="text-foreground">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Image */}
                <div className="relative">
                  <div className="relative rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="w-full aspect-[4/3] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
                  </div>
                  
                  {/* Floating Stat Card */}
                  <Card className="absolute -bottom-6 -right-6 shadow-lg border-0 bg-card">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-success" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Revenue Impact</p>
                          <p className="text-lg font-bold text-foreground">+18%</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}
